import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock3, Eye, LifeBuoy } from 'lucide-react'
import { Button } from '@agms/ui'
import { helpApi } from '../lib/api'
import type { ArticleStatus, HelpArticleHealthArticle, HelpArticleHealthCategory, HelpArticleHealthResponse } from '../lib/api'
import { apiErrorMessage, apiErrorStatus } from '../hooks/useErrorHandler'
import { PageHeader, StatCard, SurfaceCard } from '../components/ui'

export const HELP_HEALTH_FORBIDDEN_MESSAGE = "You don't have access to article health."

const STATUS_LABELS: Record<ArticleStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending review',
  published: 'Published',
  archived: 'Archived',
}

function reviewedLabel(value: string | null): string {
  if (value === null) return 'Never reviewed'
  return value.slice(0, 10)
}

const narrowColumn = 'hidden lg:table-cell'

function ArticleTable({ rows }: { rows: HelpArticleHealthArticle[] }) {
  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-500">No Help hub articles yet.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead>
          <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">Article</th>
            <th className={`px-3 py-2 ${narrowColumn}`}>Views</th>
            <th className="px-3 py-2">Tickets solved</th>
            <th className={`px-3 py-2 ${narrowColumn}`}>Suggestion opens</th>
            <th className="px-3 py-2">Last reviewed</th>
            <th className="px-3 py-2">Review</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="px-3 py-3">
                {row.slug === 'health' ? (
                  <span className="font-medium text-slate-900">{row.title}</span>
                ) : (
                  <Link to={`/help/${row.slug}`} className="font-medium text-blue-700 hover:text-blue-900">
                    {row.title}
                  </Link>
                )}
                <div className="mt-1 text-xs text-slate-500">
                  {STATUS_LABELS[row.status]}
                  {row.category_name ? ` · ${row.category_name}` : ''}
                </div>
              </td>
              <td className={`px-3 py-3 text-slate-800 ${narrowColumn}`}>{row.views}</td>
              <td className="px-3 py-3 text-slate-800">{row.tickets_solved}</td>
              <td className={`px-3 py-3 text-slate-800 ${narrowColumn}`}>{row.suggestion_opens}</td>
              <td className="px-3 py-3 text-slate-700">{reviewedLabel(row.last_reviewed_at)}</td>
              <td className="px-3 py-3">
                {row.stale ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">Stale</span>
                ) : (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-900">Current</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function dayWord(days: number): string {
  return days === 1 ? 'day' : 'days'
}

function HealthNotes({ data }: { data: HelpArticleHealthResponse }) {
  const minutes = data.minutes_per_avoided_ticket
  const openedWithoutTicket = data.totals.tickets_avoided
  const hours = data.totals.estimated_hours_saved
  const days = data.stale_after_days

  return (
    <div className="space-y-1 text-sm text-slate-700">
      <p>
        Suggestions opened with no ticket linked is {openedWithoutTicket}. Each new-ticket form counts once, for the last article opened on it, when no ticket is attached. It can still count if the link was slow, the link failed, the ticket was filed later from a new form, or the ticket was deleted afterward. Suggestions that were shown and ignored are not counted. This is not a deflection rate, and it does not prove the article answered the question.
      </p>
      <p>
        Hours saved uses a fixed {minutes}-minute guess for each of those forms, not time we measured. Right now that is {openedWithoutTicket} x {minutes} / 60 = {hours}.
      </p>
      <p>
        Stale means not reviewed in the last {days} {dayWord(days)}, or never reviewed.
      </p>
      <p>
        A review is the later of the publish day and the rejection day. An edit or an archive does not count, and sending it back for review clears the rejection date.
      </p>
    </div>
  )
}

function CategoryTable({ rows }: { rows: HelpArticleHealthCategory[] }) {
  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-500">No categories yet.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead>
          <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">Category</th>
            <th className="px-3 py-2">Articles</th>
            <th className="px-3 py-2">Published</th>
            <th className="px-3 py-2">Stale</th>
            <th className={`px-3 py-2 ${narrowColumn}`}>Views</th>
            <th className="px-3 py-2">Tickets solved</th>
            <th className={`px-3 py-2 ${narrowColumn}`}>
              Suggestion opens
              <span className="mt-1 block font-normal normal-case tracking-normal">Last article per form</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="px-3 py-3 font-medium text-slate-900">{row.name}</td>
              <td className="px-3 py-3">{row.article_count}</td>
              <td className="px-3 py-3">{row.published_count}</td>
              <td className="px-3 py-3">{row.stale_count}</td>
              <td className={`px-3 py-3 ${narrowColumn}`}>{row.views}</td>
              <td className="px-3 py-3">{row.tickets_solved}</td>
              <td className={`px-3 py-3 ${narrowColumn}`}>{row.suggestion_opens}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function HelpArticleHealthPage() {
  const queryClient = useQueryClient()
  const [staleDays, setStaleDays] = useState('')
  const [settingError, setSettingError] = useState<string | null>(null)
  const health = useQuery({
    queryKey: ['help-health'],
    queryFn: () => helpApi.health(),
  })
  const saveDays = useMutation({
    mutationFn: (days: number) => helpApi.updateHealthSettings(days),
    onSuccess: async () => {
      setSettingError(null)
      setStaleDays('')
      await queryClient.invalidateQueries({ queryKey: ['help-health'] })
    },
    onError: (error) => {
      setSettingError(apiErrorMessage(error, 'Could not save the stale-article setting.'))
    },
  })

  const forbidden = health.isError && apiErrorStatus(health.error) === 403
  const data = health.data

  return (
    <div className="space-y-6">
      <PageHeader
        title="Article health"
        subtitle="Plain counts for Help articles. Hours saved is a guess."
        action={<Link to="/help" className="text-sm font-medium text-blue-700 hover:text-blue-900">Back to Help</Link>}
      />

      {health.isLoading ? <p className="text-sm text-slate-500" aria-live="polite">Loading article health...</p> : null}
      {forbidden ? <p className="text-sm text-rose-700" role="alert">{HELP_HEALTH_FORBIDDEN_MESSAGE}</p> : null}
      {health.isError && !forbidden ? <p className="text-sm text-rose-700" role="alert">Could not load article health.</p> : null}

      {data ? (
        <>
          <section aria-label="Article health totals" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Suggestions opened with no ticket linked" value={data.totals.tickets_avoided} icon={LifeBuoy} iconColor="blue" context="Opened, and no ticket is attached" />
            <StatCard label="Estimated hours saved" value={data.totals.estimated_hours_saved} icon={Clock3} iconColor="emerald" context={`${data.totals.estimated_minutes_saved} assumed minutes`} />
            <StatCard label="Stale articles" value={data.catalog.stale_articles} icon={Clock3} iconColor="amber" context={`No review in ${data.stale_after_days}+ days`} />
            <StatCard label="Article views" value={data.catalog.views} icon={Eye} iconColor="purple" context="Lifetime page views" />
          </section>

          <section aria-label="Recorded suggestion rows" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Rows stored" value={data.totals.suggestion_rows} icon={LifeBuoy} iconColor="blue" />
            <StatCard label="Opened" value={data.totals.opened} icon={Eye} iconColor="purple" />
            <StatCard label="Opened, ticket still created" value={data.totals.opened_with_ticket} icon={LifeBuoy} iconColor="emerald" />
            <StatCard label="Opened, article later removed" value={data.totals.opened_without_article} icon={Clock3} iconColor="amber" />
            <StatCard label="Ticket created, article not opened" value={data.totals.ticket_without_open} icon={Clock3} iconColor="rose" />
          </section>

          <HealthNotes data={data} />

          <SurfaceCard padding="p-4">
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(event) => {
                event.preventDefault()
                const days = Number(staleDays || data.stale_after_days)
                saveDays.mutate(days)
              }}
            >
              <label className="text-sm font-medium text-slate-700">
                Stale after (days)
                <input
                  type="number"
                  min={1}
                  max={365}
                  className="mt-1 block w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={staleDays || String(data.stale_after_days)}
                  onChange={(event) => setStaleDays(event.target.value)}
                  disabled={!data.can_edit_stale_after_days}
                />
              </label>
              {data.can_edit_stale_after_days ? (
                <Button type="submit" disabled={saveDays.isPending}>Save</Button>
              ) : null}
              {settingError ? <p className="text-sm text-rose-700" role="alert">{settingError}</p> : null}
            </form>
          </SurfaceCard>

          <SurfaceCard>
            <h2 className="text-base font-semibold text-slate-950">Coverage by category</h2>
            <div className="mt-4">
              <CategoryTable rows={data.categories} />
            </div>
          </SurfaceCard>

          <SurfaceCard padding="p-4">
            <h2 className="text-base font-semibold text-slate-950">Coverage by author and reviewer</h2>
            <p className="mt-2 text-sm text-slate-600">
              Authors and reviewers are how many different people. No names are shown.
            </p>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <dt className="text-slate-500">Distinct authors</dt>
                <dd className="font-semibold text-slate-900">{data.authors.distinct_authors}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Articles</dt>
                <dd className="font-semibold text-slate-900">{data.authors.article_count}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Distinct reviewers</dt>
                <dd className="font-semibold text-slate-900">{data.reviewers.distinct_reviewers}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Articles with a reviewer</dt>
                <dd className="font-semibold text-slate-900">{data.reviewers.articles_with_reviewer}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Articles with no reviewer</dt>
                <dd className="font-semibold text-slate-900">{data.reviewers.articles_without_reviewer}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Never published or rejected</dt>
                <dd className="font-semibold text-slate-900">{data.reviewers.never_reviewed}</dd>
              </div>
            </dl>
          </SurfaceCard>

          <SurfaceCard>
            <h2 className="text-base font-semibold text-slate-950">Articles</h2>
            <p className="mt-1 text-sm text-slate-600">
              {data.catalog.articles} articles. {data.catalog.tickets_solved} solved links. {data.catalog.suggestion_opens} rows still tied to an article. Each new-ticket form keeps only the last article opened.
            </p>
            <div className="mt-4">
              <ArticleTable rows={data.articles} />
            </div>
          </SurfaceCard>
        </>
      ) : null}
    </div>
  )
}
